import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Hero from './components/Hero';
import CategoryTabs from './components/CategoryTabs';
import VideoGrid from './components/VideoGrid';
import FeaturedSection from './components/FeaturedSection';
import { videos, siteInfo } from '@/mocks/videos';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteInfo.name,
  url: siteInfo.url,
  description: siteInfo.description,
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: siteInfo.name,
  url: siteInfo.url,
  description: siteInfo.description,
  potentialAction: {
    '@type': 'SearchAction',
    'target': {
      '@type': 'EntryPoint',
      urlTemplate: `${siteInfo.url}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

const webpageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `${siteInfo.name} - 首页`,
  description: siteInfo.description,
  url: siteInfo.url,
  isPartOf: {
    '@type': 'WebSite',
    name: siteInfo.name,
    url: siteInfo.url,
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: '首页',
      item: siteInfo.url,
    },
  ],
};

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('hot');

  const filteredVideos = activeCategory === 'hot'
    ? videos.filter((v) => v.isHot)
    : videos.filter((v) => v.categorySlug === activeCategory);

  const categoryName = activeCategory === 'hot'
    ? '热门推荐'
    : videos.find((v) => v.categorySlug === activeCategory)?.category || '全部';

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <Hero />

          <div className="max-w-7xl mx-auto px-4 md:px-6 -mt-10 relative z-30">
            <div className="bg-background-50 rounded-lg py-4 px-4 md:px-6 shadow-sm border border-background-200/70">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-notification-3-line text-primary-500"></i>
                </div>
                <div className="overflow-hidden flex-1">
                  <div className="flex items-center gap-2 animate-marquee whitespace-nowrap">
                    <span className="text-sm text-foreground-700">
                      <strong className="text-primary-600">【热播】</strong> 庆余年第二季火热更新中，范闲归来权谋再起！
                    </span>
                    <span className="mx-4 text-foreground-300">|</span>
                    <span className="text-sm text-foreground-700">
                      <strong className="text-accent-600">【新上】</strong> 哪吒之魔童闹海重磅上映，我命由我不由天！
                    </span>
                    <span className="mx-4 text-foreground-300">|</span>
                    <span className="text-sm text-foreground-700">
                      <strong className="text-primary-600">【推荐】</strong> 斗破苍穹年番持续热播，萧炎踏上巅峰之路！
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <CategoryTabs activeCategory={activeCategory} onCategoryChange={setActiveCategory} />

          <VideoGrid videos={filteredVideos} title={`${categoryName} · 精选`} />

          <FeaturedSection />

          <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100/50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-question-answer-line text-lg text-accent-600"></i>
                </div>
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">常见问题</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { q: '桃花视频需要付费吗？', a: '桃花视频平台上的大部分内容均可免费在线观看，无需付费。我们聚合红桃视频、樱桃视频、茄子视频、糖心视频等多平台资源，为您提供免费高清观影体验。' },
                  { q: '视频播放会卡顿吗？', a: '桃花视频采用先进的CDN加速技术和多线路切换机制，确保视频播放流畅不缓冲。即使是在高峰期，也能享受稳定流畅的高清观影体验。' },
                  { q: '资源更新速度快吗？', a: '我们拥有专业的资源更新团队，热门电视剧和最新电影通常在上线后24小时内即可在桃花视频观看。每日实时更新，让您追剧不再等待。' },
                  { q: '支持哪些设备观看？', a: '桃花视频全面支持PC端、平板和手机浏览器访问，自适应不同屏幕尺寸。无论您使用什么设备，都能获得舒适的观影体验。' },
                ].map((faq) => (
                  <details key={faq.q} className="group bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5 cursor-pointer">
                    <summary className="flex items-center justify-between font-heading font-semibold text-foreground-900 text-sm md:text-base list-none">
                      {faq.q}
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line text-lg text-foreground-500 group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: [
                  { '@type': 'Question', name: '桃花视频需要付费吗？', acceptedAnswer: { '@type': 'Answer', text: '桃花视频平台上的大部分内容均可免费在线观看，无需付费。我们聚合红桃视频、樱桃视频、茄子视频、糖心视频等多平台资源，为您提供免费高清观影体验。' } },
                  { '@type': 'Question', name: '视频播放会卡顿吗？', acceptedAnswer: { '@type': 'Answer', text: '桃花视频采用先进的CDN加速技术和多线路切换机制，确保视频播放流畅不缓冲。即使是在高峰期，也能享受稳定流畅的高清观影体验。' } },
                  { '@type': 'Question', name: '资源更新速度快吗？', acceptedAnswer: { '@type': 'Answer', text: '我们拥有专业的资源更新团队，热门电视剧和最新电影通常在上线后24小时内即可在桃花视频观看。每日实时更新，让您追剧不再等待。' } },
                  { '@type': 'Question', name: '支持哪些设备观看？', acceptedAnswer: { '@type': 'Answer', text: '桃花视频全面支持PC端、平板和手机浏览器访问，自适应不同屏幕尺寸。无论您使用什么设备，都能获得舒适的观影体验。' } },
                ],
              }),
            }}
          />
        </main>
        <Footer />
      </div>
    </>
  );
}